import React, { useCallback, useEffect, useMemo, useState } from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { FormValuesModel } from '.';
import { useFormikContext } from 'formik';
import { OnboardingEntryTypes } from 'enums/onboarding/crm';
import { useEntityIntakeFormContext } from '../../EntityIntakeFormContext';
import { keyRelationshipTemplatesAPI } from 'api/crm/keyRelationshipTemplatesAPI';

// components
import FieldIssue from '../FieldIssue';
import SubmitButton from 'components/Buttons/SubmitButton';
import HideIfDisabledForm from 'components/Forms/HideIfDisabledForm';
import CountriesAutocomplete, {
  IProps as CountriesAutocompleteProps,
} from 'components/Forms/FormComponents/Autocompletes/CRM/CountriesAutocomplete';
import AvailableEntityTypes, {
  IProps as AvailableEntityTypesProps,
  EntityTypeOption,
} from './AvailableEntityTypes';
import { Col, FormField, FormSelect, Message, Row, FormSelectProps } from '@ui';

interface IProps {
  renderEntityTypeUsingSelect?: boolean;
  initialEntityTypeCategory: EntityTypeOption | null;
  clearInitialEntityTypeCategory: () => void;
}

const InnerForm = ({
  renderEntityTypeUsingSelect,
  initialEntityTypeCategory,
  clearInitialEntityTypeCategory,
}: IProps) => {
  const { t } = useTranslation(['onboarding', 'common']);
  const { getActiveIssueByFieldId } = useEntityIntakeFormContext();
  const { values, setValues, setFieldValue } =
    useFormikContext<FormValuesModel>();
  const [loadingEntityTypes, setLoadingEntityTypes] = useState(false);
  const [availableEntityTypeCategories, setAvailableEntityTypeCategories] =
    useState<EntityTypeOption[]>([]);

  useEffect(() => {
    async function fetchAvailableEntityTypes(
      country: string,
      initialOption: EntityTypeOption | null,
    ) {
      setLoadingEntityTypes(true);
      const response =
        await keyRelationshipTemplatesAPI.fetchKeyRelationshipTemplates({
          page: 1,
          limit: -1,
          isVisible: true,
          visibleFor: OnboardingEntryTypes.Organization,
          countryCode: country,
        });

      let options: EntityTypeOption[] = [];

      if (response && response.data.length) {
        options = response.data.map((e) => ({
          id: e._id,
          label: e.name,
          description: e.description,
        }));
      }

      if (initialOption) {
        const initialOptionExistsInResponse = options.find(
          (e) => e.id === initialOption.id,
        );

        if (!initialOptionExistsInResponse) {
          options.push(initialOption);
        }
      }

      const selectedTypeExistInNewArray = values.entityTypeCategory
        ? options.find((e) => e.id === values.entityTypeCategory)
        : false;

      if (values.entityTypeCategory && !selectedTypeExistInNewArray) {
        setFieldValue('entityTypeCategory', null);
        Message.info(
          t(
            'intake_form.entity_type.entity_type_is_no_longer_available_warning',
          ),
        );
      }

      setAvailableEntityTypeCategories(options);
      setLoadingEntityTypes(false);
    }

    if (values.countryOfIncorporation && !renderEntityTypeUsingSelect) {
      fetchAvailableEntityTypes(
        values.countryOfIncorporation,
        initialEntityTypeCategory,
      );

      if (initialEntityTypeCategory) {
        // Initial value of entity type category is needed only for the first render
        clearInitialEntityTypeCategory();
      }
    }
  }, [values.countryOfIncorporation, renderEntityTypeUsingSelect]);

  const disabledParentFields = useMemo(() => {
    const typeCategoryFieldIssue = getActiveIssueByFieldId(
      'entityTypeCategoryId',
    );

    return {
      countryOfIncorporation: typeCategoryFieldIssue?.isResolved === false,
    };
  }, [getActiveIssueByFieldId]);

  const handleCountryFieldClear = useCallback(() => {
    setValues({ ...values, entityTypeCategory: null });
  }, [values, setValues]);

  return (
    <>
      <Row wrap={true} justify="start">
        <Col span={14} xxl={16} xl={16} lg={18} md={24} sm={24} xs={24}>
          <FormField<CountriesAutocompleteProps>
            label={
              <FieldIssue
                fieldId="entityCountry"
                isDisabledDueToChildComments={
                  disabledParentFields.countryOfIncorporation
                }
              >
                {t('intake_form.entity_type.country_of_incorporation')}
              </FieldIssue>
            }
            disabled={disabledParentFields.countryOfIncorporation}
            name="countryOfIncorporation"
            component={CountriesAutocomplete}
            additionalProps={{
              initialValue: values.countryOfIncorporationInitialOption,
              onClear: handleCountryFieldClear,
            }}
          />
        </Col>
        {values.countryOfIncorporation ? (
          <Col span={14} xxl={16} xl={16} lg={18} md={24} sm={24} xs={24}>
            {renderEntityTypeUsingSelect ? (
              <FormField<FormSelectProps>
                label={
                  <FieldIssue fieldId="entityTypeCategoryId">
                    {t('intake_form.entity_type.entity_type')}
                  </FieldIssue>
                }
                name="entityTypeCategory"
                component={FormSelect}
                additionalProps={{
                  options: initialEntityTypeCategory
                    ? [initialEntityTypeCategory]
                    : [],
                }}
              />
            ) : (
              <FormField<AvailableEntityTypesProps>
                label={
                  <FieldIssue fieldId="entityTypeCategoryId">
                    {t('intake_form.entity_type.entity_type')}
                  </FieldIssue>
                }
                name="entityTypeCategory"
                component={AvailableEntityTypes}
                additionalProps={{
                  loading: loadingEntityTypes,
                  options: availableEntityTypeCategories,
                }}
              />
            )}
          </Col>
        ) : null}
      </Row>
      <HideIfDisabledForm>
        <SubmitButton>{t('confirm', { ns: 'common' })}</SubmitButton>
      </HideIfDisabledForm>
    </>
  );
};

export default InnerForm;
